import { Color } from '@tiptap/extension-color'
import TextStyle from '@tiptap/extension-text-style'
import ListItem from '@tiptap/extension-list-item'
import Underline from '@tiptap/extension-underline'
import StarterKit from '@tiptap/starter-kit'
import Image from '@tiptap/extension-image'
import { Markdown } from 'tiptap-markdown'
import { getHierarchicalIndexes, TableOfContents } from '@tiptap-pro/extension-table-of-contents'
import { Dropcursor } from '@tiptap/extension-dropcursor'
import OrderedList from '@tiptap/extension-ordered-list'
import GapCursor from '@tiptap/extension-gapcursor'
import { SuperchargedTableExtensions } from './supercharged-table'
import { ResizableMedia } from 'src/pages/powerdoc-view/extensions/resizableMedia'
import { Paragraph } from '@tiptap/extension-paragraph'
import { Placeholder } from '@tiptap/extension-placeholder'
import { TextAlign } from '@tiptap/extension-text-align'
import { CommentExtension } from '@sereneinserenade/tiptap-comment-extension'
import { CustomHeading, PermissionFilter } from 'src/pages/powerdoc-view/extensions/SectionDivNode'
import { InsertParagraphAfterMediaGroup, MediaGroup } from './media-group'
import api from 'src/lib/api'
import toast from 'react-hot-toast'

const ExtendedMarkdown = Markdown.configure({
  html: true,
}).extend({
  addOptions() {
    return {
      ...this.parent?.(),
      renderInline: false,
      html: true,
    }
  },

  addNodeSpecs() {
    return {
      ...this.parent?.(),
      resizableMedia: {
        toMarkdown: (state: any, node: any) => {
          const { src, alt, title, 'media-type': mediaType } = node.attrs
          if (mediaType === 'img') {
            state.write(`![${alt || ''}](${src || ''}${title ? ` "${title}"` : ''})`)
          } else if (mediaType === 'video') {
            state.write(`![video](${src || ''})`)
          }
        },
        parseMarkdown: {
          match: (token) => token?.type === 'inline' && (token.tag === 'img' || token.tag === 'video'),
          runner: (state, token) => {
            const mediaType = token.tag === 'img' ? 'img' : 'video'
            state.addNode('resizableMedia', {
              src: token.attrGet('src'),
              'media-type': mediaType,
              alt: token.attrGet('alt') || null,
              title: token.attrGet('title') || null,
            })
          },
        },
      },
    }
  },
})
export function createExtensions({
  currentUserWithPermission,
  setTableContent,
  onCommentClicked,
  setShowComment,
  isReadOnly,
}: any) {
  return [
    PermissionFilter(currentUserWithPermission, isReadOnly),
    ExtendedMarkdown.configure({
      html: true,
    }),
    CommentExtension.configure({
      HTMLAttributes: {
        class: 'my-comment',
      },
      onCommentActivated: (commentId) => {
        if (commentId) {
          const element = document.querySelector('[data-comment-id="' + commentId + '"]')
          const rect = element?.getBoundingClientRect()
          onCommentClicked?.(commentId, rect)
        } else {
          setShowComment?.(false)
        }
      },
    }),
    Color.configure({ types: [TextStyle.name, ListItem.name] }),
    GapCursor,
    Placeholder.configure({
      placeholder: 'Write something…',
    }),
    Paragraph,
    Underline,
    TextStyle,
    OrderedList,
    CustomHeading,
    StarterKit.configure({
      bulletList: {
        keepMarks: true,
        keepAttributes: true,
      },
      orderedList: {
        keepMarks: true,
        keepAttributes: true,
      },
      heading: false,
    }),
    TableOfContents.configure({
      getIndex: getHierarchicalIndexes,
      onUpdate(content) {
        setTableContent(content)
      },
    }),
    Dropcursor.configure({
      color: 'blue',
      width: 3,
      class: 'prosemirror-dropcursor',
    }),
    ResizableMedia.configure({
      uploadFn: async (img: File) => {
        const formData = new FormData()
        formData.append('file', img)
        const res = await api.post('/powerdocs/attachement', formData)
        if (res.data.url) {
          return res.data.url
        } else {
          toast.error('Unable to upload the attachement.')
        }
      },
    }),
    MediaGroup,
    Image.configure({
      allowBase64: true,
    }),
    ...SuperchargedTableExtensions,
    TextAlign.configure({
      types: ['heading', 'paragraph'],
    }),
    InsertParagraphAfterMediaGroup,
  ]
}
